import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { Dropdown, Modal, DropdownButton } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import { store } from 'store/index'
import { SHOW_LOADER, HIDE_LOADER } from 'store/loader/actions'
import ExternalPosBalance from './ExternalPosBalance'
import PhotoScanModal from 'pages/main/documentsIn/PhotoScanModal'
import ReactSelect from 'pages/parts/ReactSelect'
import DocumentTemplate from 'pages/parts/DocumentTemplate'
import { SET_TRANSFER_DATA } from "store/transferData/action";
import { httpOk, GET, POST, DELETE, PUT } from 'helpers/api'
import { handleDecimalKeyDown } from 'helpers/decimalKey'
import { formatDate, formatMoney, returnMinDate, notify } from 'helpers/helpers'
import { bestMatch, ELEMENT_MATCH_THRESHOLD } from 'helpers/documentInPhoto'

function CreateUpdate() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const tbodyRef = useRef()
	const scrollRef = useRef()

	const reduxAccount = useSelector(state => state.account)
	const reduxTransferData = useSelector(state => state.transferData)

	const [data, setData] = useState({
		'items': [],
		'from_wms_id': reduxAccount.wms_id ?? null,
		'to_pos_id': null,
		'to_wms_id': null,
		'note': '',
		'markup_type': 0, // 0 — наценка от себестоимости, 1 — от цены продажи (на весь документ)
		'total_sale_price': 0,
		'created_at': formatDate(new Date(), 'yyyy-MM-dd'),
	})

	// Тип наценки: себестоимость + наценка / от цены продажи / себестоимость без наценки
	const markupTypes = [
		{ 'id': 0, 'name': t('from_cost_price') },
		{ 'id': 1, 'name': t('from_sale_price') },
		{ 'id': 2, 'name': t('cost_price_no_markup') },
	]
	const [addQuantity, setAddQuantity] = useState(1)
	const [poses, setPoses] = useState([])
	const [wms, setWms] = useState([])
	const [externalWms, setExternalWms] = useState([])
	const [items, setItems] = useState([])
	const [itemsDuplicate, setItemsDuplicate] = useState([])
	const [modalConfirm, setModalConfirm] = useState(false)
	const [modalTemplate, setModalTemplate] = useState({ 'bool': false, 'templates': [] })
	const [modalTemplateCRUD, setModalTemplateCRUD] = useState({ 'bool': false, 'name': "", 'products': [] })
	const [templateProducts, setTemplateProducts] = useState([])
	const [modalMarkup, setModalMarkup] = useState({ 'bool': false, 'products': [] })
	const [markupFilters, setMarkupFilters] = useState({ 'search': '', 'in_product': null })
	const [productTypes, setProductTypes] = useState([])
	const [modalExterntalBalance, setModalExterntalBalance] = useState({ 'bool': false, 'products': [] })
	const [modalPhoto, setModalPhoto] = useState(false)

	// Отправленный документ (-2) правится, пока получатель его не принял: состав,
	// количество, цены и примечание. Получателя, склад-отправителя и дату не трогаем —
	// на них уже записаны движения склада и долг внешней точки.
	const isSent = data.status === -2

	// Перемещение, оформленное продажей через чек, склад не двигало — товар списал чек.
	// Такой документ не правится: его состав принадлежит чеку, а не перемещению.
	const isChequeDocument = isSent && data.has_movements === false

	// isDraft — сохранить черновиком (остатки не двигаются), иначе провести документ
	async function createUpdate(stage = 1, isDraft = false) {
		var dataCopy = { ...data }
		dataCopy.error = false

		if (dataCopy.items.length === 0) {
			notify(t('add_elements'))
			return
		}

		for (let i = 0; i < dataCopy.items.length; i++) {
			if (!dataCopy.items[i]['quantity']) {
				dataCopy.error = true
			}
		}

		// Строка с фото могла остаться без товара — сохранять такую нечего
		for (let i = 0; i < dataCopy.items.length; i++) {
			if (!dataCopy.items[i]['product_id']) {
				notify(`${t('check_filled_inputs')}: ${i + 1}`)
				return
			}
		}

		var valueArr = dataCopy.items.map(function (item) { return item.product_id });
		var isDuplicate = valueArr.some(function (item, idx) {
			return valueArr.indexOf(item) !== idx
		});

		if (isDuplicate) {
			notify(t('identical_elements'))
			return
		}

		if (dataCopy.error) {
			notify(t('check_filled_inputs'))
			return
		}

		dataCopy.is_draft = isDraft

		// Проведение подтверждаем модалкой, черновик сохраняем сразу
		if (!isDraft && (stage === 1 || stage === 2)) {
			setModalConfirm(true)
			return
		}

		var response
		if (id) {
			response = await PUT('/service/wms-transfer-pos', dataCopy, { loader: true })
		} else {
			response = await POST('/service/wms-transfer-pos', dataCopy, { loader: true })
		}

		if (httpOk(response)) navigate('/service/wms-transfer-pos')
	}

	async function updateMarkup() {
		const response = await PUT('/products-markup', modalMarkup.products, { loader: true })

		if (httpOk(response)) {
			// Наценки изменились — перечитываем справочник товаров,
			// иначе строки документа считаются по старым наценкам из памяти
			const responseItems = data.from_wms_id
				? await GET(`/service/wms-transfer-pos-items/${data.from_wms_id}`, {}, { loader: true })
				: null
			if (httpOk(responseItems)) {
				setItems(responseItems.data)
				setItemsDuplicate(responseItems.data)

				// Подтягиваем свежие наценки в уже добавленные строки и пересчитываем цену продажи
				var dataCopy = { ...data }
				for (let i = 0; i < dataCopy.items.length; i++) {
					const product = responseItems.data.find(item => item.product_id === dataCopy.items[i]['product_id'])
					if (!product) continue

					dataCopy.items[i]['markup'] = product.markup
					dataCopy.items[i]['markup_round'] = product.markup_round
				}
				recalcSalePrices(dataCopy)
			}

			toggleModalMarkup(false)
		}
	}

	function addToList(length = 1) {
		var dataCopy = JSON.parse(JSON.stringify(data))

		for (let i = 0; i < length; i++) {
			dataCopy.items.push({
				'id': '',
				'sale_price': '',
				'quantity': '',
			})
		}
		countTotalAmount(dataCopy)

		setTimeout(() => {
			const byId = document.getElementById(`input-select-${dataCopy.items.length}`)
			byId.select()
		}, 200);

	}

	async function deleteItem(index) {
		var dataCopy = JSON.parse(JSON.stringify(data))
		dataCopy.items.splice(index, 1)

		countTotalAmount(dataCopy)
	}

	// markupType — тип наценки на весь документ: 0 — себестоимость + наценка, 1 — от цены продажи, 2 — себестоимость без наценки
	function calculateItemSalePrice(item, markupType = data.markup_type) {
		const costPrice = Number(item.cost_price) || 0
		const markup = Number(item.markup) || 0
		const markupRound = Number(item.markup_round) || 0
		const productSalePrice = Number(item.product_sale_price ?? item.sale_price) || 0

		// Передаём по себестоимости: наценка и округление не применяются,
		// иначе сумма документа разойдётся с себестоимостью склада-отправителя
		if (Number(markupType) === 2) {
			return costPrice
		}

		// База, на которую накручивается наценка
		const markupBase = Number(markupType) === 1 ? productSalePrice : costPrice

		let calculatedSalePrice = markup
			? markupBase + (markupBase * markup / 100)
			: productSalePrice

		if (markupRound && calculatedSalePrice) {
			calculatedSalePrice = Number(
				(Math.round(calculatedSalePrice / markupRound) * markupRound).toFixed(3)
			)
		}

		return calculatedSalePrice
	}

	function countTotalAmount(dataCopy) {
		dataCopy.total_quantity = 0
		dataCopy.total_sale_price = 0
		for (let i = 0; i < dataCopy.items.length; i++) {
			dataCopy.total_quantity += Number(dataCopy.items[i]['quantity'])
			dataCopy.total_sale_price += Number(dataCopy.items[i]['quantity']) * Number(dataCopy.items[i]['sale_price'])
		}

		setData(dataCopy)
	}

	function handleSelectWms(option) {
		setData({ ...data, 'items': [], 'total_quantity': 0, 'total_sale_price': 0 })
		getProducts(option.id)
	}

	function handleSelectExternalPos(option) {
		// Тип наценки запоминается под каждого получателя — подставляем сохранённый
		const stored = localStorage.getItem(`wms_transfer_markup_type_${option.id}`)
		const markupType = stored !== null ? Number(stored) : (data.markup_type ?? 0)

		var dataCopy = { ...data, 'to_pos_id': option.id, 'markup_type': markupType }
		recalcSalePrices(dataCopy, markupType)
		getExternalWms(option.id)
	}

	function handleSelectMarkupType(option) {
		const markupType = option ? option.id : 0

		var dataCopy = { ...data, 'markup_type': markupType }
		recalcSalePrices(dataCopy, markupType)

		// Запоминаем выбор под текущего получателя
		if (data.to_pos_id) {
			localStorage.setItem(`wms_transfer_markup_type_${data.to_pos_id}`, markupType)
		}
	}

	// Пересчитывает цену продажи по всем строкам под выбранный тип наценки
	function recalcSalePrices(dataCopy, markupType = dataCopy.markup_type) {
		for (let i = 0; i < dataCopy.items.length; i++) {
			if (dataCopy.items[i].quantity) {
				dataCopy.items[i].sale_price = calculateItemSalePrice(dataCopy.items[i], markupType)
			}
		}
		countTotalAmount(dataCopy)
	}

	function handleInputChange(e, index) {
		let dataCopy = { ...data };

		dataCopy.items[index][e.target.name] = e.target.value;

		if (e.target.name === 'quantity') {
			dataCopy.items[index]['sale_price'] = calculateItemSalePrice(dataCopy.items[index], dataCopy.markup_type);
		}

		countTotalAmount(dataCopy);
	}

	function handleChangeMarkup(e, productId) {
		var markupProductsCopy = [...modalMarkup?.products]
		const productIndex = markupProductsCopy.findIndex(item => item.product_id === productId)
		if (productIndex === -1) return

		markupProductsCopy[productIndex][e.target.name] = e.target.value

		setModalMarkup({ ...modalMarkup, 'products': markupProductsCopy })
	}

	function handleChangeAll(e) {
		var markupProductsCopy = [...modalMarkup?.products]

		if (e.target.name === 'markup') {
			for (let i = 0; i < markupProductsCopy.length; i++) {
				markupProductsCopy[i]['markup'] = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')
			}
		}

		if (e.target.name === 'markup_round') {
			for (let i = 0; i < markupProductsCopy.length; i++) {
				markupProductsCopy[i]['markup_round'] = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')
			}
		}

		setModalMarkup({ ...modalMarkup, 'products': markupProductsCopy })
	}

	// REACTSELECT LOGIC
	function handleSelectSearch(value) {
		if (!value) {
			setItems([...itemsDuplicate])
			return
		}
		var elementsDuplicateCopy = [...itemsDuplicate]

		var filteredItems = elementsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setItems(filteredItems)
	}

	function handleSelectElement(option, index) {
		var dataCopy = { ...data }
		const quantity = dataCopy.items[index]?.quantity ?? ''

		dataCopy.items[index] = {
			...option,
			product_sale_price: option.sale_price,
			quantity,
			sale_price: quantity ? calculateItemSalePrice({ ...option, product_sale_price: option.sale_price }, dataCopy.markup_type) : '',
		}

		countTotalAmount(dataCopy)
	}

	function closeReactSelect() {
		setTimeout(() => {
			var elementsCopy = [...items]
			for (let i = 0; i < elementsCopy.length; i++) {
				elementsCopy[i]['selected'] = false
			}
			if (elementsCopy[0] && elementsCopy[0]['selected'])
				elementsCopy[0]['selected'] = true
			setItems(elementsCopy)
		}, 100);
	}

	function handleTableKeyDown(event) {
		event.stopPropagation();
		var currentIndex = items.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			var elementsCopy = [...items]
			elementsCopy[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					if (currentIndex === 0) {
						elementsCopy[items.length - 1].selected = true
						currentIndex = items.length - 1
					} else {
						elementsCopy[currentIndex - 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6) - 59.2
					break;
				case "ArrowDown":
					if (currentIndex === items.length - 1) {
						elementsCopy[0].selected = true
						currentIndex = 0
					} else {
						elementsCopy[currentIndex + 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6)
					break;
				default: break;
			}

			setItems(elementsCopy)
		}
	}
	// REACTSELECT LOGIC

	// TEMPLATE LOGIC
	async function toggleModalTemplate(bool) {
		if (bool) {
			const response = await GET('/documents-templates', {}, { loader: true })
			if (httpOk(response)) {
				setModalTemplate({ ...modalTemplate, 'bool': bool, 'templates': response.data })
			}
		} else {
			setModalTemplate({ ...modalTemplate, 'bool': bool, 'templates': [] })
		}
	}

	async function toggleModalTemplateCRUD(bool) {
		if (bool) {
			const response = await GET('/documents-templates-all-products', {}, { loader: true })
			if (httpOk(response)) {
				setTemplateProducts(response.data)
				setModalTemplateCRUD({ ...modalTemplateCRUD, 'bool': bool, 'name': "", 'products': [] })
			}
		} else {
			setModalTemplateCRUD({ ...modalTemplateCRUD, 'bool': bool, 'name': "", 'products': [] })
		}
	}

	async function handleSelectTemplate(item) {
		const response = await GET(`/documents-templates-products/${item.id}`, {}, { loader: true })
		if (!httpOk(response)) return

		var dataCopy = { ...data }
		var temporaryItems = []
		// Из шаблона берём только те товары, которые есть на выбранном складе
		for (let i = 0; i < response.data.length; i++) {
			const product = itemsDuplicate.find(element => element.product_id === response.data[i]['product_id'])
			if (!product) continue

			temporaryItems.push({
				...product,
				'product_sale_price': product.sale_price,
				'sale_price': '',
				'quantity': '',
			})
		}

		dataCopy.items = temporaryItems
		countTotalAmount(dataCopy)
		toggleModalTemplate(false)
	}

	async function deleteTemplate(item) {
		const response = await DELETE(`/documents-templates/${item.id}`, {}, { loader: true })
		if (httpOk(response)) toggleModalTemplate(false)
	}
	// TEMPLATE LOGIC

	async function getProductTypes() {
		const response = await GET('/helper/product-types')
		if (httpOk(response)) {
			setProductTypes(response.data.filter(item => [0, 1, 2, 3].includes(item.id)))
		}
	}

	function getFilteredMarkupProducts() {
		return (modalMarkup?.products || []).filter(item => {
			const matchesSearch = !markupFilters.search
				|| item.name?.toLowerCase().includes(markupFilters.search.toLowerCase())
			const matchesType = markupFilters.in_product === null
				|| markupFilters.in_product === ''
				|| item.product_type_id === markupFilters.in_product

			return matchesSearch && matchesType
		})
	}

	function handleMarkupFilterChange(name, value) {
		setMarkupFilters(prev => ({ ...prev, [name]: value }))
	}

	async function toggleModalMarkup(bool) {
		if (bool) {
			setMarkupFilters({ 'search': '', 'in_product': null })
			const response = await GET('/service/wms-transfer-pos-products', {}, { loader: true })
			if (httpOk(response)) {
				setModalMarkup({ 'bool': bool, 'products': response.data })
			}
		} else {
			setModalMarkup({ 'bool': false, 'products': [] })
			setMarkupFilters({ 'search': '', 'in_product': null })
		}
	}

	async function toggleModalExternalBalance(bool) {
		setModalExterntalBalance({ ...modalExterntalBalance, 'bool': bool, 'products': [] })
	}

	// ПЕРЕМЕЩЕНИЕ ПО ФОТО
	// Раскладываем распознанную накладную по форме: примечание и позиции. Цену продажи считаем
	// по наценке, как при ручном вводе, — цену с фото не берём.
	// Что не совпало со справочником — остаётся строкой с подсказкой, товар выбирает пользователь.
	function applyRecognized(recognized) {
		if (!recognized) return

		var dataCopy = { ...data }

		if (recognized.document_number && !dataCopy.note) {
			dataCopy.note = `№${recognized.document_number}`
		}

		const usedProductIds = []
		const photoItems = []
		for (let i = 0; i < (recognized.items?.length ?? 0); i++) {
			const recognizedItem = recognized.items[i]
			const name = String(recognizedItem.name ?? '').trim()
			if (!name) continue

			const available = itemsDuplicate.filter(item => !usedProductIds.includes(item.product_id))
			const product = bestMatch(name, available, ELEMENT_MATCH_THRESHOLD)
			const quantity = Number(recognizedItem.quantity) > 0 ? String(Number(recognizedItem.quantity)) : ''

			if (product) {
				usedProductIds.push(product.product_id)
				const photoItem = {
					...product,
					'product_sale_price': product.sale_price,
					'suggested_name': name,
					'quantity': quantity,
				}
				photoItem.sale_price = quantity ? calculateItemSalePrice(photoItem, dataCopy.markup_type) : ''
				photoItems.push(photoItem)
			} else {
				photoItems.push({ 'id': '', 'product_id': '', 'suggested_name': name, 'sale_price': '', 'quantity': quantity })
			}
		}

		// Ненайденные — наверх, чтобы сразу было видно, что нужно доработать руками
		photoItems.sort((a, b) => (a.product_id ? 1 : 0) - (b.product_id ? 1 : 0))

		dataCopy.items = photoItems
		countTotalAmount(dataCopy)
	}

	const photoItemsCount = data.items.filter(item => item.suggested_name).length
	const photoNotFoundCount = data.items.filter(item => item.suggested_name && !item.product_id).length
	// ПЕРЕМЕЩЕНИЕ ПО ФОТО

	function handlePassed(items) {
		var dataCopy = { ...data }
		const mergedArray = [...dataCopy.items, ...items];
		dataCopy.items = mergedArray

		countTotalAmount(dataCopy)
	}

	async function getData() {
		store.dispatch(SHOW_LOADER());
		
		try {
			var response
			response = await GET('/helper/integration-access-poses', {}, {})
			if (httpOk(response)) setPoses(response.data)

			response = await GET(`/helper/wms`, {}, {})
			if (httpOk(response)) setWms(response.data)

			if (id) {
				response = await GET(`/service/wms-transfer-pos/${id}`, {}, {})
				if (httpOk(response)) {
					var documentData = { ...response.data }

					// Черновик и ещё не принятый документ можно дополнять — подгружаем
					// товары склада, иначе в строку нечего выбрать
					if ((documentData.status === 0 || documentData.status === -2) && documentData.from_wms_id) {
						const responseItems = await GET(`/service/wms-transfer-pos-items/${documentData.from_wms_id}`, {}, {})
						if (httpOk(responseItems)) {
							setItems(responseItems.data)
							setItemsDuplicate(responseItems.data)

							// Наценка в строках документа не хранится — подтягиваем её из
							// справочника, иначе пересчёт цены продажи уронит её в ноль
							documentData.items = documentData.items.map(item => {
								const product = responseItems.data.find(element => element.product_id === item.product_id)
								if (!product) return item

								return {
									...item,
									'markup': product.markup,
									'markup_round': product.markup_round,
									'product_sale_price': product.sale_price,
								}
							})
						}
					}

					countTotalAmount(documentData)
				}
			}

			// Перемещение на основании прихода везёт товар с того склада, куда он пришёл
			if (!id) await getProducts(reduxTransferData?.from_wms_id ?? reduxAccount.wms_id)
		} finally {
			store.dispatch(HIDE_LOADER());
		}
	}

	async function getExternalWms(external_pos_id) {
		const response = await GET(`/helper/external-wms?external_pos_id=${external_pos_id}`, {}, { loader: true })
		if (httpOk(response)) setExternalWms(response.data)
	}

	async function getProducts(wms_id = reduxAccount.wms_id) {
		// Склад может быть не задан у пользователя — тогда справочник товаров
		// подгрузится после выбора склада в селекте, а не улетит в /items/null
		if (!wms_id) {
			setItems([])
			setItemsDuplicate([])
			return
		}

		var response = await GET(`/service/wms-transfer-pos-items/${wms_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setItems(response.data)
			setItemsDuplicate(response.data)

			var dataCopy = { ...data }
			dataCopy.from_wms_id = wms_id

			// Предзаполнение: заказ другой точки (id) или приход-основание (source_document_in_id)
			if (reduxTransferData?.id || reduxTransferData?.source_document_in_id) {
				// Тип наценки, запомненный под этого получателя
				const rememberedType = localStorage.getItem(`wms_transfer_markup_type_${reduxTransferData?.external_pos_id}`)
				const markupType = rememberedType !== null ? Number(rememberedType) : (dataCopy.markup_type ?? 0)
				dataCopy.markup_type = markupType

				var temporaryItems = []
				for (let i = 0; i < reduxTransferData?.items?.length; i++) {
					if (!reduxTransferData.items[i]['product_id']) {
						temporaryItems.push({
							'sale_price': '',
							'quantity': '',
							'id': ''
						})
					}

					for (let j = 0; j < response.data.length; j++) {
						if (reduxTransferData.items[i]['product_id'] === response.data[j]['product_id']) {
							const transferItem = {
								...response.data[j],
								product_sale_price: response.data[j].sale_price,
								order_quantity: reduxTransferData.items[i]['quantity'],
								quantity: reduxTransferData.items[i]['quantity'],
							}
							transferItem.sale_price = calculateItemSalePrice(transferItem, markupType)
							temporaryItems.push(transferItem)
						}
					}
				}
				if (reduxTransferData?.id) {
					dataCopy.document_order_id = reduxTransferData.id
					dataCopy.to_pos_id = reduxTransferData?.external_pos_id
				}

				if (reduxTransferData?.source_document_in_id) {
					dataCopy.source_document_in_id = reduxTransferData.source_document_in_id
					dataCopy.note = reduxTransferData.note ?? ''

					// Удалённый с тех пор товар в справочник склада не попадает — молча
					// терять строку прихода нельзя, говорим, что её нет
					const missing = reduxTransferData.items.filter(item =>
						!response.data.some(product => product.product_id === item.product_id))
					if (missing.length > 0) notify(t('transfer_document_in_missing', { count: missing.length }))
				}

				dataCopy.items = temporaryItems
			}

			countTotalAmount(dataCopy)
			dispatch(SET_TRANSFER_DATA({}))
		}
	}

	useEffect(() => {
		getData()
		getProductTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('transfer')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{!id ? t('create') : (data.status === 0 ? t('draft') : t('edit'))}</h6>
					</div>
				</div>
				<div className="d-flex gap-2">
					{/* Отдельной кнопкой, а не пунктом меню — иначе про перемещение по фото никто не узнает */}
					{!id &&
						<button type="button" className="btn btn-outline-primary btn-rounded"
							onClick={() => setModalPhoto(true)}>
							<i className="uil uil-camera me-1"></i>
							{t('transfer_by_photo')}
						</button>
					}
					<DropdownButton title={t('action')}>
						<Dropdown.Item onClick={() => toggleModalExternalBalance(true)}>
							{t('external_balance')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => toggleModalMarkup(true)}>
							{t('markup')}
						</Dropdown.Item>
						{!id &&
							<Dropdown.Item onClick={() => toggleModalTemplate(true)}>
								{t('templates')}
							</Dropdown.Item>
						}
						<Dropdown.Item onClick={() => toggleModalTemplateCRUD(true)}>
							{t('create_template')}
						</Dropdown.Item>
					</DropdownButton>
				</div>
			</div>

			<form autoComplete="off">
				<div className="card mb-3">
					<div className="card-body">
						<div className="row">
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('date')}<span className="required-mark">*</span></label>
									<div className="calendar-input">
										<DatePicker
											showMonthDropdown
											showYearDropdown
											dropdownMode="select"
											yearDropdownItemNumber={2}
											disabled={isSent}
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={data.created_at ? new Date(data.created_at) : ''}
											onChange={(date) => setData({ ...data, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
											minDate={returnMinDate(new Date(), 20)}
											maxDate={new Date()} />
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('from_warehouse')}<span className="required-mark">*</span></label>
									<Select
										isDisabled={isSent}
										options={wms}
										value={wms.find(option => option.id === data.from_wms_id)}
										onChange={(option) => handleSelectWms(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('external_pos')}<span className="required-mark">*</span></label>
									<Select
										isDisabled={isSent}
										options={poses}
										value={poses.find(option => option.id === data.to_pos_id)}
										onChange={(option) => handleSelectExternalPos(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('markup_type')}</label>
									<Select
										options={markupTypes}
										value={markupTypes.find(option => option.id === data.markup_type)}
										onChange={(option) => handleSelectMarkupType(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('external_wms')}</label>
									<Select
										isClearable
										options={externalWms}
										value={externalWms.find(option => option.id === data.to_wms_id)}
										onChange={(option) => setData({ ...data, 'to_wms_id': option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('note')}</label>
									<input type="text" className="form-control" name="note"
										value={data.note ?? ''}
										onChange={(e) => setData({ ...data, 'note': e.target.value })} />
								</div>
							</div>
							<div className="col-md-1">
								<div className="form-group">
									<label>{t('order')} ID</label>
									<input type="number" className="form-control" name="document_order_id"
										disabled
										value={data.document_order_id ?? ''}
										onChange={(e) => setData({ ...data, 'document_order_id': e.target.value })} />
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="card">
					<div className="card-body">
						{photoNotFoundCount > 0 &&
							<div className="alert alert-warning">
								<div className="fw-600">
									{photoNotFoundCount} / {photoItemsCount} {t('positions_not_found')}
								</div>
								<div>{t('transfer_positions_not_found_hint')}</div>
							</div>
						}

						<div className="table-responsive">
							<table className="table table-bordered">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '30%' }}>{t('name2')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('ordered')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('balance2')}</th>
										<th style={{ 'width': '10%' }} className="text-start">{t('unit')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('quantity')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('cost_price')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('sale_price')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
										<th style={{ 'minWidth': '33px' }}></th>
									</tr>
								</thead>
								<tbody>
									{data?.items?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td>
												<ReactSelect
													search={handleSelectSearch}
													value={item.name}
													index={index}
													scrollRef={scrollRef}
													options={items}
													onClose={() => closeReactSelect()}
													selectItem={(option) => handleSelectElement(option, index)}
													onKeyUp={(e) => {
														if (e.keyCode === 40 || e.keyCode === 38) {
															handleTableKeyDown(e)
														}
													}}
													table={
														<table className="table mb-0">
															<tbody ref={tbodyRef}>
																{items.map((selectItem, selectIndex) => (
																	<tr
																		id={selectItem.id}
																		className={`cursor ${((selectItem.selected || selectItem.product_id === item.element_id) && 'selected')}`}
																		tabIndex={-1}
																		key={selectIndex}
																		onClick={() => handleSelectElement(selectItem, index)}
																		onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																		<td>{selectItem.name}</td>
																		<td>{selectItem.product_type_name}</td>
																		<td className="text-end text-nowrap">{formatMoney(selectItem.cost_price)}</td>
																	</tr>
																))}
															</tbody>
														</table>
													}
												/>
												{/* Наименование с фото — видно, пока позицию не сопоставили со справочником */}
												{item.suggested_name &&
													<small className={item.product_id ? 'text-muted' : 'text-danger'}>
														{item.product_id ? t('from_photo') : t('not_found_in_handbook')}: {item.suggested_name}
													</small>
												}
											</td>
											<td className="text-end vertical-middle">
												{formatMoney(item.order_quantity, 2)}
											</td>
											<td className="text-end vertical-middle">
												{formatMoney(item.balance, 2)}
											</td>
											<td className="text-start vertical-middle">
												{item.unit_name}
											</td>
											<td className="td-input" style={!item.quantity ? { 'borderBottom': '2px solid red' } : {}}>
												<input
													type="text"
													name="quantity"
													className="form-control text-end"
													placeholder="0"
													value={item.quantity ?? ''}
													onChange={(e) => handleInputChange(e, index)}
													onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleInputChange(event, index))} />
											</td>
											<td className="text-end vertical-middle">
												{formatMoney(item.cost_price)}
											</td>
											<td className="td-input" style={!item.sale_price ? { 'borderBottom': '2px solid red' } : {}}>
												<input
													type="text"
													name="sale_price"
													className="form-control text-end"
													placeholder="0"
													value={item.sale_price ?? ''}
													onChange={(e) => handleInputChange(e, index)}
													onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleInputChange(event, index))}
													onKeyUp={(event) => {
														if (event.key === 'Enter') { addToList() }
													}} />
											</td>
											<td className="vertical-middle text-end text-nowrap">
												{formatMoney(item.quantity * item.sale_price)}
											</td>
											<td className="vertical-middle">
												<div className="d-flex justify-content-center">
													<div className="table-action-button table-action-danger-button"
														onClick={() => deleteItem(index)}>
														<i className="uil uil-times"></i>
													</div>
												</div>
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td className="td-input">
											<input
												type="text"
												className="form-control text-center"
												style={{ 'padding': '5px 0' }}
												placeholder="xxx"
												value={addQuantity}
												onChange={(e) => setAddQuantity(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, ''))}
											/>
										</td>
										<td colSpan={1} style={{ 'padding': 0 }}>
											<div className="d-flex">
												<div className="w-100">
													<button type="button" className="btn-sm btn-primary w-100"
														onClick={() => {
															setAddQuantity(1)
															addToList(addQuantity)
														}}>
														{t('add')}
													</button>
												</div>
											</div>
										</td>
										<td></td>
										<td></td>
										<td></td>
										<td className="text-end"><b>{formatMoney(data.total_quantity, 2)}</b></td>
										<td></td>
										<td></td>
										<td className="text-end"><b>{formatMoney(data.total_sale_price)}</b></td>
										<td></td>
									</tr>
								</tfoot>
							</table>
						</div>

						{isChequeDocument &&
							<div className="text-danger text-center mb-2">
								{t('transfer_created_by_cheque')}
							</div>
						}

						<div className="btn-wrapper gap-2">
							<button type="button" className="btn btn-warning btn-rounded btn-wide" tabIndex="-1"
								onClick={() => navigate(-1)}>
								{t('cancel')}
							</button>
							{/* Отправленный документ в черновик не возвращается: движения уже записаны */}
							{!isSent &&
								<button type="button" className="btn btn-outline-primary btn-rounded btn-wide" tabIndex="-1"
									disabled={!(data.from_wms_id && data.to_pos_id && data.items.length > 0)}
									onClick={() => createUpdate(3, true)}>
									{t('save_draft')}
								</button>
							}
							<button type="button" className="btn btn-primary btn-rounded btn-wide" tabIndex="-1"
								disabled={isChequeDocument || !(data.from_wms_id && data.to_pos_id && data.items.length > 0)}
								onClick={() => createUpdate(1)}>
								{t('confirm')}
							</button>
						</div>
					</div>
				</div>
			</form >

			{/* MODAL CONFIRM */}
			<Modal Modal show={modalConfirm} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => setModalConfirm(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('confirm')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => setModalConfirm(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => createUpdate(3)}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL CONFIRM */}

			{/* MODAL TEMPLATE */}
			<Modal show={modalTemplate.bool} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModalTemplate(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('templates')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '100%' }}>{t('name2')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{modalTemplate?.templates?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td onClick={() => handleSelectTemplate(item)}>
											{item.name}
										</td>
										<td>
											<div className="d-flex justify-content-end">
												<div className="table-action-button table-action-danger-button"
													onClick={() => deleteTemplate(item)}>
													<i className="uil uil-times"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL TEMPLATE */}

			{/* MODAL TEMPLATE CRUD */}
			{modalTemplateCRUD.bool &&
				<DocumentTemplate
					products={templateProducts}
					modalTemplateCRUD={modalTemplateCRUD}
					setModalTemplateCRUD={setModalTemplateCRUD}
					toggleModalTemplateCRUD={toggleModalTemplateCRUD}>
				</DocumentTemplate>
			}
			{/* MODAL TEMPLATE CRUD */}

			{/* MODAL PRODUCTS MARKUP */}
			<Modal show={modalMarkup.bool} animation={false} centered size="xl"
				dialogClassName="update-modal-width" onHide={() => toggleModalMarkup(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('products')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive" style={{ 'height': '500px', 'overflow': 'auto' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '30%' }} className="td-input">
										<input
											autoComplete="off"
											type="text"
											name="search"
											className="form-control"
											placeholder={t('name2')}
											value={markupFilters.search}
											onChange={(e) => handleMarkupFilterChange('search', e.target.value)} />
									</th>
									<th className="text-nowrap">{t('unit')}</th>
									<th style={{ 'width': '20%' }} className="td-input">
										<Select
											isClearable
											classNamePrefix="react-select"
											placeholder={t('type')}
											options={productTypes}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
											value={productTypes.find(option => option.id === markupFilters.in_product) || null}
											onChange={(option) => handleMarkupFilterChange('in_product', option ? option.id : null)}
										/>
									</th>
									<th style={{ 'width': '20%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ 'width': '20%' }} className="text-end td-input">
										<input
											autoComplete="off"
											type="number"
											name="markup"
											className="form-control text-end"
											placeholder={`${t('markup')}%`}
											onChange={(e) => handleChangeAll(e)} />
									</th>
									<th style={{ 'width': '20%' }} className="text-end td-input">
										<input
											autoComplete="off"
											type="number"
											name="markup_round"
											className="form-control text-end"
											placeholder={`${t('round')}`}
											onChange={(e) => handleChangeAll(e)} />
									</th>
								</tr>
							</thead>
							<tbody>
								{getFilteredMarkupProducts().map((item, index) => (
									<tr key={item.product_id}>
										<td className="text-center">{index + 1}</td>
										<td className="td-ellipsis">{item.name}</td>
										<td>{item.unit_name}</td>
										<td>{item.product_type_name}</td>
										<td className="text-end">{formatMoney(item.cost_price)}</td>
										<td className="td-input">
											<input
												autoComplete="off"
												type="text"
												name="markup"
												className="form-control text-end"
												placeholder="0"
												value={item.markup ?? ''}
												onChange={(e) => handleChangeMarkup(e, item.product_id)} />
										</td>
										<td className="td-input">
											<input
												autoComplete="off"
												type="text"
												name="markup_round"
												className="form-control text-end"
												placeholder="0"
												value={item.markup_round ?? ''}
												onChange={(e) => handleChangeMarkup(e, item.product_id)} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="btn-wrapper gap-2">
						<button type="button" className="btn btn-warning btn-rounded btn-wide" tabIndex="-1"
							onClick={() => toggleModalMarkup(false)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary btn-rounded btn-wide" tabIndex="-1"
							onClick={() => updateMarkup()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL PRODUCTS MARKUP */}

			{/* MODAL PHOTO */}
			<PhotoScanModal
				show={modalPhoto}
				mode="transfer"
				posId={reduxAccount.pos_id}
				onClose={() => setModalPhoto(false)}
				onApply={applyRecognized}>
			</PhotoScanModal>
			{/* MODAL PHOTO */}

			<ExternalPosBalance
				data={data}
				handlePassed={handlePassed}
				poses={poses}
				parentItems={items}
				setModalExterntalBalance={setModalExterntalBalance}
				modalExterntalBalance={modalExterntalBalance}
				toggleModalExternalBalance={toggleModalExternalBalance}>
			</ExternalPosBalance>
		</>
	)
}

export default CreateUpdate